import { Children, forwardRef, isValidElement, useMemo, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import DOMPurify from "isomorphic-dompurify";
import { cn } from "@/lib/utils.js";

const ACTION_SECTION_HEADING = /^what to do next\b/i;

/** @type {import("hast-util-sanitize").Schema} */
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: [...defaultSchema.attributes.a, "target", "rel"],
  },
};

/**
 * @param {import("react").ReactNode} children
 * @returns {string}
 */
function flattenText(children) {
  return Children.toArray(children)
    .map((child) => {
      if (typeof child === "string" || typeof child === "number") {
        return String(child);
      }
      if (isValidElement(child) && child.props?.children != null) {
        return flattenText(child.props.children);
      }
      return "";
    })
    .join("");
}

/**
 * @param {string|undefined|null} href
 * @returns {string|undefined}
 */
function sanitizeMarkdownHref(href) {
  if (!href) return undefined;

  const sanitized = DOMPurify.sanitize(href, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  }).trim();
  if (!sanitized) return undefined;

  const lowered = sanitized.toLowerCase();
  if (
    lowered.startsWith("javascript:")
    || lowered.startsWith("data:")
    || lowered.startsWith("vbscript:")
  ) {
    return undefined;
  }

  if (sanitized.startsWith("#") || sanitized.startsWith("/")) {
    return sanitized;
  }

  try {
    const parsed = new URL(sanitized, "https://example.com");
    if (["http:", "https:", "mailto:"].includes(parsed.protocol)) {
      return sanitized;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

/** @typedef {{ inActionSection: boolean }} MarkdownParseState */

/**
 * @param {import("react").RefObject<MarkdownParseState>} parseStateRef
 * @returns {import("react-markdown").Components}
 */
function createMarkdownComponents(parseStateRef) {
  /**
   * @param {"h1"|"h2"|"h3"|"h4"} tag
   * @param {string} className
   */
  function heading(tag, className) {
    const Tag = tag;
    return ({ children, ...props }) => {
      const text = flattenText(children).trim();
      parseStateRef.current.inActionSection = ACTION_SECTION_HEADING.test(text);
      return (
        <Tag className={className} {...props}>
          {children}
        </Tag>
      );
    };
  }

  return {
    h1: heading(
      "h1",
      "mb-3 mt-0 text-base font-semibold text-foreground first:mt-0"
    ),
    h2: heading(
      "h2",
      "mb-2 mt-4 border-b border-border pb-1 text-sm font-semibold text-foreground first:mt-0"
    ),
    h3: heading("h3", "mb-2 mt-3 text-sm font-semibold text-foreground"),
    h4: heading("h4", "mb-1.5 mt-2 text-sm font-medium text-foreground"),
    p: ({ children, ...props }) => (
      <p className='mb-2 last:mb-0 leading-relaxed text-foreground' {...props}>
        {children}
      </p>
    ),
    ul: ({ children, ...props }) => {
      const checklist = parseStateRef.current.inActionSection;
      return (
        <ul
          className={cn(
            "mb-3 last:mb-0",
            checklist
              ? "rca-action-checklist list-none space-y-2 pl-0"
              : "list-disc space-y-1 pl-5"
          )}
          {...props}>
          {children}
        </ul>
      );
    },
    ol: ({ children, ...props }) => {
      const checklist = parseStateRef.current.inActionSection;
      return (
        <ol
          className={cn(
            "mb-3 last:mb-0",
            checklist
              ? "rca-action-checklist list-none space-y-2 pl-0"
              : "list-decimal space-y-1 pl-5"
          )}
          {...props}>
          {children}
        </ol>
      );
    },
    li: ({ children, ...props }) => {
      if (parseStateRef.current.inActionSection) {
        return (
          <li
            className='flex items-start gap-2.5 text-foreground'
            {...props}>
            <span
              className='mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 border-border bg-card shadow-sm'
              aria-hidden
            />
            <span className='min-w-0 flex-1 leading-relaxed'>{children}</span>
          </li>
        );
      }

      return (
        <li className='leading-relaxed text-foreground' {...props}>
          {children}
        </li>
      );
    },
    strong: ({ children, ...props }) => (
      <strong className='font-semibold text-foreground' {...props}>
        {children}
      </strong>
    ),
    em: ({ children, ...props }) => (
      <em className='italic text-foreground' {...props}>
        {children}
      </em>
    ),
    blockquote: ({ children, ...props }) => (
      <blockquote
        className='mb-3 border-l-4 border-brand-cyan/60 pl-3 italic text-foreground last:mb-0'
        {...props}>
        {children}
      </blockquote>
    ),
    code: ({ children, className, ...props }) => {
      const isBlock = Boolean(className);
      if (isBlock) {
        return (
          <code
            className={cn(
              "block overflow-x-auto rounded-md bg-muted px-3 py-2 font-mono text-xs text-foreground",
              className
            )}
            {...props}>
            {children}
          </code>
        );
      }
      return (
        <code
          className='rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-foreground'
          {...props}>
          {children}
        </code>
      );
    },
    pre: ({ children, ...props }) => (
      <pre
        className='mb-3 overflow-x-auto rounded-md bg-muted p-3 last:mb-0'
        {...props}>
        {children}
      </pre>
    ),
    a: ({ href, children, ...props }) => {
      const safeHref = sanitizeMarkdownHref(href);
      if (!safeHref) {
        return <span className='text-foreground'>{children}</span>;
      }
      return (
        <a
          href={safeHref}
          target='_blank'
          rel='noopener noreferrer'
          className='font-medium text-brand-cyan underline decoration-brand-cyan/40 underline-offset-2 hover:text-brand-cyan/80'
          {...props}>
          {children}
        </a>
      );
    },
    hr: (props) => <hr className='my-4 border-border' {...props} />,
    table: ({ children, ...props }) => (
      <div className='mb-3 overflow-x-auto last:mb-0'>
        <table
          className='min-w-full border-collapse text-left text-xs text-foreground'
          {...props}>
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }) => (
      <thead className='border-b border-border bg-muted' {...props}>
        {children}
      </thead>
    ),
    th: ({ children, ...props }) => (
      <th className='px-2 py-1.5 font-semibold text-foreground' {...props}>
        {children}
      </th>
    ),
    td: ({ children, ...props }) => (
      <td className='border-t border-border px-2 py-1.5' {...props}>
        {children}
      </td>
    ),
  };
}

/**
 * Renders LLM markdown safely with checklist styling under "What To Do Next".
 *
 * @param {{ markdown: string, className?: string }} props
 */
const RcaAiAnalysisMarkdown = forwardRef(function RcaAiAnalysisMarkdown(
  { markdown, className },
  ref
) {
  const parseStateRef = useRef(/** @type {MarkdownParseState} */ ({
    inActionSection: false,
  }));

  // Reset before each ReactMarkdown parse so streaming updates don't inherit
  // checklist styling from the previous render's final section state.
  parseStateRef.current.inActionSection = false;

  const components = useMemo(
    () => createMarkdownComponents(parseStateRef),
    []
  );
  const remarkPlugins = useMemo(() => [remarkGfm], []);
  const rehypePlugins = useMemo(
    () => [[rehypeSanitize, sanitizeSchema]],
    []
  );

  return (
    <div
      ref={ref}
      className={cn(
        "prose prose-sm max-w-none text-left dark:prose-invert",
        className
      )}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={components}>
        {markdown}
      </ReactMarkdown>
    </div>
  );
});

export default RcaAiAnalysisMarkdown;
